import {useCallback, useMemo, useState} from 'react'
import type {CopilotReviewerRow} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import {
  normalizeCopilotReviewEffortSource,
  readCopilotReviewEffortAcceptance,
  readCopilotReviewEffortSubmission,
  type CopilotReviewEffortRequest,
  type CopilotReviewEffortSource,
} from '@github-ui/pull-request-page-data-tooling/copilot-review-effort'
import {normalizeCopilotReviewEffort, type CopilotReviewEffortValue} from './copilot-review-effort'

export function useCopilotReviewEffortDecision(
  row: CopilotReviewerRow | undefined,
  scope = '',
  reviewState?: string,
  completedReviewId?: string,
  dataSource?: 'cached' | 'network',
) {
  const resolved = normalizeCopilotReviewEffort(row?.selectedReviewEffort)
  const resolvedSource = normalizeCopilotReviewEffortSource(row?.selectedReviewEffortSource)
  const persistedEffort = resolvedSource === 'previous_review' ? resolved : undefined
  const [state, setState] = useState<{
    scope: string
    reviewState?: string
    completedReviewId?: string
    persistedEffort?: CopilotReviewEffortValue
    revision: number
    changedLocally: boolean
    awaitingInitialRefresh: boolean
    awaitingReviewChange: boolean
    decision?: {effort: CopilotReviewEffortValue; selected: boolean; source?: CopilotReviewEffortSource}
  }>({
    scope,
    reviewState,
    completedReviewId,
    persistedEffort,
    revision: 0,
    changedLocally: false,
    awaitingInitialRefresh: dataSource === 'cached',
    awaitingReviewChange: false,
  })
  const decision = state.scope === scope ? state.decision : undefined
  const reviewChanged =
    reviewState !== undefined && state.reviewState !== undefined && reviewState !== state.reviewState
  const hasNewCompletedReview = completedReviewId !== undefined && completedReviewId !== state.completedReviewId
  // A dismissed request can change persisted intention without creating a completed review.
  const persistedEffortChanged = persistedEffort !== undefined && persistedEffort !== state.persistedEffort
  if (
    state.scope !== scope ||
    (!decision && resolved) ||
    (!state.changedLocally &&
      ((state.awaitingInitialRefresh && dataSource === 'network') ||
        (persistedEffortChanged &&
          (!state.awaitingReviewChange ||
            state.persistedEffort !== undefined ||
            persistedEffort !== decision?.effort)) ||
        (reviewChanged && (!state.awaitingReviewChange || hasNewCompletedReview))))
  ) {
    setState({
      scope,
      reviewState,
      completedReviewId,
      persistedEffort,
      revision: state.revision + 1,
      changedLocally: false,
      awaitingInitialRefresh: dataSource === 'cached',
      awaitingReviewChange: false,
      decision: resolved ? {effort: resolved, selected: false, source: resolvedSource} : undefined,
    })
  } else if (
    reviewState !== undefined &&
    (reviewState !== state.reviewState || persistedEffort !== state.persistedEffort)
  ) {
    // Tracking a review change must not invalidate a still-current local request.
    setState({
      ...state,
      reviewState,
      completedReviewId,
      persistedEffort,
      awaitingReviewChange: state.awaitingReviewChange && reviewState === state.reviewState,
    })
  }

  const reviewerId = row?.reviewer ? Number(row.reviewer.id) : undefined
  const shownEffort = decision?.effort ?? resolved
  const explicitEffort = decision?.selected ? decision.effort : undefined
  const canCaptureRequest = Boolean(row?.requestable && row.reviewEffortOptions.length > 0)
  const stageEffort = useCallback(
    (value: CopilotReviewEffortValue) =>
      setState(current =>
        current.scope === scope && current.decision?.effort !== value
          ? {
              ...current,
              revision: current.revision + 1,
              changedLocally: true,
              awaitingInitialRefresh: false,
              awaitingReviewChange: false,
              decision: {effort: value, selected: true, source: 'explicit_selection'},
            }
          : current,
      ),
    [scope],
  )
  const captureRequest = useCallback((): CopilotReviewEffortRequest | undefined => {
    if (!canCaptureRequest) return
    setState(current =>
      current.scope === scope && current.revision === state.revision && current.awaitingInitialRefresh
        ? {...current, awaitingInitialRefresh: false}
        : current,
    )
    return {
      fields: decision
        ? {
            review_effort_client_version: '2',
            review_effort_level: decision.effort,
            review_effort_level_selected: decision.selected,
            ...(decision.source ? {review_effort_source: decision.source} : {}),
          }
        : {review_effort_client_version: '2', review_effort_level_selected: false},
      acceptResponse: response => {
        const submitted = readCopilotReviewEffortSubmission(response)
        if (submitted) {
          setState(current =>
            current.scope === scope && current.revision === state.revision
              ? {
                  ...current,
                  revision: current.revision + 1,
                  changedLocally: false,
                  awaitingInitialRefresh: false,
                  awaitingReviewChange: current.reviewState === state.reviewState,
                  decision: current.decision
                    ? {...current.decision, selected: false}
                    : {
                        effort: submitted.review_effort_level,
                        selected: false,
                        source: submitted.review_effort_source,
                      },
                }
              : current,
          )
          return
        }
        const accepted = readCopilotReviewEffortAcceptance(response)
        const value = normalizeCopilotReviewEffort(accepted?.review_effort_level)
        if (!value) return
        setState(current =>
          current.scope === scope && current.revision === state.revision
            ? {
                ...current,
                revision: current.revision + 1,
                changedLocally: false,
                awaitingInitialRefresh: false,
                awaitingReviewChange: current.reviewState === state.reviewState,
                decision: {effort: value, selected: false, source: accepted?.review_effort_source},
              }
            : current,
        )
      },
    }
  }, [canCaptureRequest, decision, scope, state])

  return useMemo(
    () => ({
      reviewerId,
      shownEffort,
      explicitEffort,
      stageEffort,
      captureRequest,
    }),
    [captureRequest, explicitEffort, reviewerId, shownEffort, stageEffort],
  )
}
